import React, { useState, useRef, useEffect } from 'react';
import { parseISO, format, startOfWeek, endOfWeek, eachDayOfInterval, isSameDay, startOfMonth } from 'date-fns';
import { ACTIVITY_LEVELS } from '../utils/data';

const ActivityCalendar = ({ data = [], theme = {}, weeksVertical = true, precedingMonths = 2 }) => {
  const [cellSize] = useState(16); // Cell size in pixels
  const [visibleWeeks, setVisibleWeeks] = useState(new Set());
  const containerRef = useRef(null);
  
  // Centralized theme from ACTIVITY_LEVELS
  const levelColorMap = ACTIVITY_LEVELS.reduce((acc, l) => {
    acc[l.level] = l.color;
    return acc;
  }, {});

  // Get the date range from data
  const getDateRange = () => {
    if (data.length === 0) {
      // If no data, show a reasonable default range (last 3 months)
      const today = new Date();
      const threeMonthsAgo = new Date(today.getFullYear(), today.getMonth() - 3, 1);
      const start = startOfWeek(threeMonthsAgo, { weekStartsOn: 0 });
      const end = endOfWeek(today, { weekStartsOn: 6 });
      return { start, end };
    }

    const dates = data.map(item => parseISO(item.date));
    const earliestDate = new Date(Math.min(...dates));
    
    // Calculate start date: precedingMonths months before the earliest activity date
    const start = new Date(earliestDate);
    start.setMonth(start.getMonth() - precedingMonths);
    start.setDate(1); // Start of the month
    
    // End date is today (not beyond)
    const end = new Date();
    end.setHours(23, 59, 59, 999); // End of today
    
        
    return { start, end };
  };

  // Create a map of activity data
  const activityMap = data.reduce((acc, item) => {
    acc[item.date] = item;
    return acc;
  }, {});

  // Generate all days in the range
  const { start, end } = getDateRange();
  
  // Align start to beginning of week for proper calendar display
  const calendarStart = startOfWeek(start, { weekStartsOn: 0 });
  
  // Use today as the end date, not the end of the week
  const today = new Date();
  today.setHours(23, 59, 59, 999); // End of today
  const calendarEnd = today;
  
  const allDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  
  // Filter out any days beyond today (double-check)
  const days = allDays.filter(day => day <= today);

  // Group days by weeks
  const weeks = [];
  let currentWeek = [];
  
  days.forEach((day, index) => {
    currentWeek.push(day);
    if (currentWeek.length === 7 || index === days.length - 1) {
      weeks.push([...currentWeek]);
      currentWeek = [];
    }
  });

  // Check which weeks are visible in the viewport
  useEffect(() => {
    const checkVisibleWeeks = () => {
      if (!containerRef.current) return;
      
      const container = containerRef.current;
      const monthLabelElements = container.querySelectorAll('[data-month-label-index]');
      const visible = new Set();
      const containerRect = container.getBoundingClientRect();
      
      monthLabelElements.forEach((element) => {
        const elementRect = element.getBoundingClientRect();
        const weekIndex = parseInt(element.getAttribute('data-month-label-index'));
        
        // Check if the month label is actually visible within the container
        // The element's left edge should be within the container's bounds
        if (elementRect.left >= containerRect.left && elementRect.left < containerRect.right) {
          visible.add(weekIndex);
        }
      });
      
      setVisibleWeeks(visible);
    };

    // Check on scroll and resize
    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', checkVisibleWeeks);
      window.addEventListener('resize', checkVisibleWeeks);
      checkVisibleWeeks(); // Initial check
      
      return () => {
        container.removeEventListener('scroll', checkVisibleWeeks);
        window.removeEventListener('resize', checkVisibleWeeks);
      };
    }
  }, [weeks.length]);

  // Get the week indices where a new month starts
  const getMonthLabelMap = () => {
    const monthLabelMap = {};
    
    weeks.forEach((week, weekIndex) => {
      // Check if this week contains the 1st of any month
      const hasFirstOfMonth = week.some(day => day.getDate() === 1);
      
      if (hasFirstOfMonth) {
        // Find the first day of the month in this week
        const firstOfMonth = week.find(day => day.getDate() === 1);
        if (firstOfMonth && visibleWeeks.has(weekIndex)) {
          monthLabelMap[weekIndex] = format(firstOfMonth, 'MMM yyyy').toLowerCase();
        }
      }
    });
    
    return monthLabelMap;
  };

  // Replace getActivityLevel to use ACTIVITY_LEVELS
  const getActivityLevel = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const activity = activityMap[dateStr];
    const count = activity ? activity.count || 0 : 0;
    // Find the matching level
    const found = ACTIVITY_LEVELS.find(l => count >= l.min && count <= l.max);
    return found ? found.level : 0;
  };

  const getTooltipText = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const activity = activityMap[dateStr];
    if (activity) {
      return `${format(date, 'MMM dd, yyyy')}: ${activity.count} activities`;
    }
    return format(date, 'MMM dd, yyyy');
  };

  if (weeksVertical) {
    // Vertical weeks layout - each week is a column, weeks are arranged horizontally
    const monthLabelMap = getMonthLabelMap();
    // Find the last day in the days array (should be today)
    const lastDayStr = format(days[days.length - 1], 'yyyy-MM-dd');
    
    return (
      <div style={{ padding:'7px',fontFamily: 'monospace', fontSize: '12px' }}>
        {/* Main scrollable container */}
        <div 
          ref={containerRef}
          className="activity-calendar-container"
          style={{ 
            display: 'flex', 
            flexDirection: 'column',
            overflowX: 'auto',
            scrollbarWidth: 'none', // Hide scrollbar
            msOverflowStyle: 'none', // Hide scrollbar in IE/Edge
            paddingBottom: '0px',
            maxWidth: '100%'
          }}
        >
          {/* Month labels - scrolls with the calendar */}
          <div style={{ 
            display: 'flex', 
            gap: '3px',
            paddingBottom: '4px',
            minWidth: 'fit-content'
          }}>
            {weeks.map((_, weekIndex) => (
              <div
                key={weekIndex}
                data-month-label-index={weekIndex}
                style={{
                  width: `${cellSize}px`,
                  minWidth: `${cellSize}px`,
                  height: `${cellSize + 2}px`,
                  display: 'flex',
                  alignItems: 'flex-end',
                  justifyContent: 'flex-start',
                }}
              >
                {monthLabelMap[weekIndex] ? (
                  <span
                    style={{
                      fontSize: '10px',
                      color: 'var(--text-muted)',
                      fontWeight: 500,
                      textAlign: 'left',
                      lineHeight: 1.1,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {monthLabelMap[weekIndex]}
                  </span>
                ) : null}
              </div>
            ))}
          </div>
          
          {/* Calendar grid */}
          <div style={{ 
            display: 'flex', 
            gap: '3px',
            paddingBottom: '8px', // Space for scrollbar
            minWidth: 'fit-content'
          }}>
            {weeks.map((week, weekIndex) => (
              <div 
                key={weekIndex} 
                data-week-index={weekIndex}
                style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: '3px',
                  minWidth: 'fit-content'
                }}
              >
                {week.map((day, dayIndex) => {
                  const level = getActivityLevel(day);
                  const dayStr = format(day, 'yyyy-MM-dd');
                  const isLastDay = dayStr === lastDayStr;
                  return (
                    <div
                      key={dayIndex}
                      title={getTooltipText(day)}
                      style={{
                        width: `${cellSize}px`,
                        height: `${cellSize}px`,
                        backgroundColor: levelColorMap[level],
                        borderRadius: '2px',
                        border: isLastDay ? '2px solid rgba(8, 129, 243, 0.86)' : 'none',
                        cursor: 'pointer',
                        transition: 'opacity 0.2s',
                        opacity: level === 0 ? 0.4 : 1,
                      }}
                      onClick={() => {
                                              }}
                      onMouseEnter={(e) => {
                        e.target.style.opacity = level === 0 ? '1' : '1';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.opacity = level === 0 ? '0.4' : '1';
                      }}
                    />
                  );
                })}
              </div>
            ))}
          </div>
        </div>
        
        {/* Legend */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '8px', 
          marginTop: '8px',
          fontSize: '11px',
          color: '#666'
        }}>
          <span>-</span>
          {ACTIVITY_LEVELS.map(level => (
            <div
              key={level.level}
              title={level.label}
              style={{
                width: `${cellSize}px`,
                height: `${cellSize}px`,
                backgroundColor: levelColorMap[level.level],
                borderRadius: '2px'
              }}
            />
          ))}
          <span>+</span>
        </div>
      </div>
    );
  }

  // Default horizontal weeks layout
  return (
    <div style={{ fontFamily: 'monospace', fontSize: '12px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
        {weeks.map((week, weekIndex) => (
          <div key={weekIndex} style={{ display: 'flex', gap: '3px' }}>
            {week.map((day, dayIndex) => {
              const level = getActivityLevel(day);
              const isToday = isSameDay(day, new Date());
              
              return (
                <div
                  key={dayIndex}
                  title={getTooltipText(day)}
                  style={{
                    width: `${cellSize}px`,
                    height: `${cellSize}px`,
                    backgroundColor: levelColorMap[level],
                    borderRadius: '2px',
                    border: isToday ? '1px solid #000' : 'none',
                    cursor: 'pointer',
                    transition: 'opacity 0.2s',
                    opacity: level === 0 ? 0.7 : 1,
                  }}
                  onClick={() => {
                                      }}
                  onMouseEnter={(e) => {
                    e.target.style.opacity = level === 0 ? '0.8' : '1';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.opacity = level === 0 ? '0.7' : '1';
                  }}
                />
              );
            })}
          </div>
        ))}
      </div>
      
      {/* Legend */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '8px', 
        marginTop: '8px',
        fontSize: '11px',
        color: '#666'
      }}>
        <span>-</span>
        {ACTIVITY_LEVELS.map(level => (
          <div
            key={level.level}
            style={{
              width: `${cellSize}px`,
              height: `${cellSize}px`,
              backgroundColor: levelColorMap[level.level],
              borderRadius: '2px'
            }}
          />
        ))}
        <span>+</span>
      </div>
    </div>
  );
};

export default ActivityCalendar; 